import React, {useEffect, useState} from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import {Container, Row, Col, Form} from 'react-bootstrap';
import {useNavigate} from "react-router-dom";
import {BarLoader} from "react-spinners";
import {
    Box, Step, StepButton, Stepper, FormControl as MFormControl,
    RadioGroup, FormControlLabel, Radio, TextField, FormLabel, Select, InputLabel, MenuItem, Checkbox, Button as MButton
} from "@mui/material";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import {useTheme} from "@mui/material/styles";
import {useApiRequest} from "../../../common/hooks/useApiRequest";


function DeckCreationWizard() {


    const theme = useTheme();
    const [decks, setDecks] = useState([]);
    const [currentTab, setCurrentTab] = useState(0);
    const [deckUploadingStatus, setDeckUploadingStatus] = useState('none');
    const [deckUploadingErrorMessage, setDeckUploadingErrorMessage] = useState('');
    const [deckFile, setDeckFile] = useState(null);
    const [deckContents, setDeckContents] = useState([]);
    const [swapWordSides, setSwapWordSides] = useState(false);
    const [saveOption, setSaveOption] = useState(''); // new/existing
    const [deckName, setDeckName] = useState('');
    const [reviewMode, setReviewMode] = useState('both');
    const [deckToAddTo, setDeckToAddTo] = useState(null);
    const [loadingDeckToAddTo, setLoadingDeckToAddTo] = useState(false);
    const [dontAddExistingCards, setDontAddExistingCards] = useState(false);

    const [fieldSelectionPhase, setFieldSelectionPhase] = useState(0);
    const [frontColumn, setFrontColumn] = useState(-1);
    const [backColumn, setBackColumn] = useState(-1);
    const [auxColumns, setAuxColumns] = useState([]);

    const [numWordsToCreate, setNumWordsToCreate] = useState(0);
    const [publishingDeck, setPublishingDeck] = useState(false);
    const [publishingDeckErrorMessage, setPublishingDeckErrorMessage] = useState('');

    const navigate = useNavigate();

    const stepTitles = ["Upload deck file", "Choose deck action", "Classify card sides", "Select words to add"];

    const stepViews = [uploadDeckView, deckOptionsView, fieldSelectionView, wordSelectionView]

    const {getRequest, postRequest} = useApiRequest();

    useEffect(() => {
        getRequest('users/1/decks').then(setDecks);
    }, []);


    const revertToTab0 = () => {
        setCurrentTab(0);
        setDeckUploadingStatus('none');
        setDeckUploadingErrorMessage('');
        setDeckFile(null);
        setDeckContents([]);
        setSwapWordSides(false);
        setSaveOption('');
        setDeckName('');
        setReviewMode('both');
        setDeckToAddTo(null);
        setLoadingDeckToAddTo(false);
        setDontAddExistingCards(false);
        setNumWordsToCreate(0);
        setFieldSelectionPhase(0);
        setFrontColumn(-1);
        setBackColumn(-1);
        setAuxColumns([]);
    };

    const revertToTab1 = () => {
        setCurrentTab(1);
        setSwapWordSides(false);
        setSaveOption('');
        setDeckName(deckFile.name.substring(0, deckFile.name.lastIndexOf('.')));
        setReviewMode('both');
        setDeckToAddTo(null);
        setLoadingDeckToAddTo(false);
        setDontAddExistingCards(false);
        setNumWordsToCreate(deckContents.length);
        setFieldSelectionPhase(0);
        setFrontColumn(-1);
        setBackColumn(-1);
        setAuxColumns([]);
    }

    const revertToTab2 = () => {
        setCurrentTab(2);
        setFieldSelectionPhase(0);
        setFrontColumn(-1);
        setBackColumn(-1);
        setAuxColumns([]);
    }

    const revertToTab = tab => [revertToTab0, revertToTab1, revertToTab2][tab]()

    const handleFileChange = (event) => {
        setDeckFile(event.target.files[0]);
        setDeckName(event.target.files[0].name.substring(0, event.target.files[0].name.lastIndexOf('.')));
        setDeckUploadingStatus("uploading");
        const formData = new FormData();
        formData.append('deck', event.target.files[0]);
        formData.append('data', JSON.stringify({userId: 1}))

        fetch(`${process.env.REACT_APP_BACKEND_ENTRYPOINT}/validate-deck`, {
            method: 'POST',
            body: formData
        }).then(response => {
            if (response.status === 400) {
                response.json().then(data => {
                    setDeckUploadingStatus('error');
                    setDeckUploadingErrorMessage(data.error)
                });
            }
            else if (response.status === 201) {
                response.json().then(data => {
                    const words = JSON.parse(data.words);
                    words.forEach(word => {
                        word.add = true
                        word.alreadyExists = false;
                    });
                    setDeckContents(words);
                    setNumWordsToCreate(words.length);
                    setDeckUploadingStatus('success');
                });
            }
            else {
                setDeckUploadingStatus('error');
                setDeckUploadingErrorMessage('could not upload deck');
            }
        });
    };

    const handleDeckToAddToChanged = deck => {
        setDeckToAddTo(deck);
        setLoadingDeckToAddTo(true);
        getRequest(`vocabulary?deck_id=${deck.deckId}`)
            .then(words => {
                const existing = words.map(word => word.word);
                deckContents.forEach(word => word.alreadyExists = existing.includes(word.word));
                // existingDeckWords.current = words;
                setLoadingDeckToAddTo(false);
            });
    };

    const handleDontAddExistingChanged = () => {
        const newValue = !dontAddExistingCards;
        setNumWordsToCreate(newValue ? deckContents.filter(word => !word.alreadyExists).length : deckContents.length);
        setDontAddExistingCards(newValue);
    };

    const designateColumn = columnIdx => {
        if (fieldSelectionPhase === 0) {
            setFrontColumn(columnIdx);
            setFieldSelectionPhase(1);
        }
        else if (fieldSelectionPhase === 1) {
            setBackColumn(columnIdx);
            setFieldSelectionPhase(2);
        }
        else if (auxColumns.includes(columnIdx)) {
            setAuxColumns(auxColumns.filter((col, idx) => idx !== columnIdx));
        }
        else {
            setAuxColumns([...auxColumns, columnIdx]);
        }
    }


    const handleWordCheckChanged = word => {
        setNumWordsToCreate(numWordsToCreate + (word.add ? -1 : 1));
        word.add = !word.add;
    };

    const saveNewWords = () => {
        setPublishingDeck(true);
        if (saveOption === 'new') {
            postRequest('publish-deck', {
                userId: 1,
                deckName: deckName,
                tempIds: deckContents.filter(word => word.add && !(dontAddExistingCards && word.alreadyExists)).map(word => word.tempId),
                // exchangeWithTranslation: swapWordSides,
                reviewMode: reviewMode,
                frontIndex: frontColumn,
                backIndex: backColumn,
                auxIndexes: auxColumns
            }).then(() => {
                setPublishingDeck(false);
                navigate('/');
            })
        }
        else if (saveOption === 'existing') {
            fetch(`${process.env.REACT_APP_BACKEND_ENTRYPOINT}/append-deck`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: 1,
                    deckId: deckToAddTo.deckId,
                    tempIds: deckContents.filter(word => word.add && !(dontAddExistingCards && word.alreadyExists)).map(word => word.tempId),
                    frontIndex: frontColumn,
                    backIndex: backColumn,
                    auxIndexes: auxColumns
                })
            }).then(response => {
                setPublishingDeck(false);
                if (response.status === 400) {
                    response.json().then(data => {
                        setPublishingDeckErrorMessage(data.error)
                    });
                }
                else if (response.status === 201) {
                    response.json().then(() => {
                        navigate('/');
                    });
                }
                else {
                    setPublishingDeckErrorMessage('could not upload deck');
                }
            });
        }
    };

    return (
        <Container fluid className="container-fluid px-0 pt-5 d-flex flex-column align-items-center">
            <h1 className="text-center">Import Deck</h1>
            <div className="mt-4">{stepsView()}</div>
        </Container>
    )

    function stepsView() {
        return (
            <Box sx={{ maxWidth: 1200}}>
                <Stepper activeStep={currentTab} sx={{mb: 5}}>
                    {stepTitles.map((stepTitle, stepIndex) => (
                        <Step key={stepTitle}>
                            <StepButton onClick={() => revertToTab(stepIndex)}>{stepTitle}</StepButton>
                        </Step>
                    ))}
                </Stepper>
                {stepViews[currentTab]()}
            </Box>
        );
    }

    function uploadDeckView() {
        return (
            <MFormControl fullWidth={true}>
                <p className="mb-2">Supported file format: apkg</p>
                <a href="https://ankiweb.net/shared/decks">Get decks here</a>
                {/*<FormControl className="my-3" type="file" onChange={handleFileChange}/>*/}
                <MButton variant="outlined" component="label" sx={{mt: 3, mb: 1}}>
                    <FileUploadIcon/>
                    {deckFile ? deckFile.name : "Upload file"}
                    <input onChange={handleFileChange} type="file" hidden/>
                </MButton>
                {deckUploadingStatus === 'uploading' && <div>
                    <BarLoader className="w-100" color={theme.palette.info.main} loading={true}/>
                    <p>uploading deck...</p>
                </div>}
                {deckUploadingStatus === 'error' &&
                    <p className="text-danger fst-italic">{deckUploadingErrorMessage}</p>}
                {deckUploadingStatus === 'success' &&
                    <p className="text-success fst-italic">Deck uploaded successfully</p>}
                {deckUploadingStatus === 'success' && <MButton fullWidth={true} variant="contained" onClick={() => setCurrentTab(1)}>Next</MButton>}
            </MFormControl>)
    }

    function deckOptionsView() {
        return (
            <MFormControl fullWidth={true}>
                <RadioGroup>
                    <FormControlLabel control={<Radio/>} label="Save as new deck" value="new" onChange={() => setSaveOption("new")}/>
                    <FormControlLabel control={<Radio/>} label="Add cards to existing deck" value="existing" onChange={() => setSaveOption("existing")}/>
                </RadioGroup>
                {saveOption === 'new' && (
                    <div className="my-3 ms-4 w-100">
                        <FormLabel>Deck name:</FormLabel>
                        <TextField variant="standard" fullWidth={true} value={deckName} onChange={(e) => setDeckName(e.target.value)}/>
                        <FormLabel sx={{mt: 3}}>Review Mode:</FormLabel>
                        <RadioGroup defaultValue="both">
                            <FormControlLabel control={<Radio/>} label="Foreign to English" value="f2b" onChange={() => setReviewMode("f2b")}/>
                            <FormControlLabel control={<Radio/>} label="English to foreign" value="b2f" onChange={() => setReviewMode("b2f")}/>
                            <FormControlLabel control={<Radio/>} label="Both" value="both" onChange={() => setReviewMode("both")}/>
                        </RadioGroup>
                    </div>
                )}
                {saveOption === 'existing' && (
                    <div className="my-3 ms-4 w-100">
                        <MFormControl fullWidth>
                            <InputLabel>Deck</InputLabel>
                            <Select label="Deck">
                                {decks.map((deck, index) => (
                                    <MenuItem key={index} value={deck.deckName} onClick={() => handleDeckToAddToChanged(deck)}>
                                        {deck.deckName}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormControlLabel control={<Checkbox />}
                                              onChange={handleDontAddExistingChanged}
                                              label="Don't add cards already in that deck"/>
                        </MFormControl>
                        {loadingDeckToAddTo && (<div className="mt-3">
                            <BarLoader color={theme.palette.info.main} loading={true} className="w-100 mt-1"/>
                            <p>verifying deck</p>
                        </div>)}
                    </div>
                )}
                {!(loadingDeckToAddTo || !saveOption || (saveOption === 'existing' && !deckToAddTo)) &&
                    <MButton fullWidth={false} variant="contained" onClick={() => setCurrentTab(2)}>Next</MButton>
                }
            </MFormControl>
        )
    }

    function fieldSelectionView() {
        return (
            <div>
                {(fieldSelectionPhase <= 1 || deckContents[0].parts.length > 2) &&
                    <p className="mt-4">{"Currently selecting: "}
                        <strong className={fieldSelectionPhase === 0 ? "text-primary" : fieldSelectionPhase === 1 ? "text-success" : "text-secondary"}>
                            {fieldSelectionPhase === 0 ? "Front" : fieldSelectionPhase === 1 ? "Back" : "Auxiliary"}
                        </strong>
                    </p>
                }
                <p className="mt-4">{
                    fieldSelectionPhase === 0 ? "Select a column as the front side of the cards. This should be the column with the foreign words" :
                    fieldSelectionPhase === 1 ? "Select a column as the back side of the cards. This should be the column with the native words" :
                    deckContents[0].parts.length > 2 ? "Select all additional columns you want to keep. They will be shown in the info tab during reviews" : ""
                }</p>
                <Row className="d-flex justify-content-evenly align-items-center">
                    {deckContents && deckContents[0] && deckContents[0].parts.map((_, idx) => (
                        <Col>
                            {frontColumn === idx && <div>
                                <strong className="text-primary">Front</strong>
                                {/*<Button className="bi-arrow-counterclockwise ms-2 btn-sm" onClick={revertFrontColumn}></Button>*/}
                            </div>}
                            {backColumn === idx && <div>
                                <strong className="text-success">Back</strong>
                                {/*<Button className="bi-arrow-counterclockwise btn-success ms-2 btn-sm" onClick={revertBackColumn}></Button>*/}
                            </div>}
                            {auxColumns.includes(idx) && (
                                <strong className="text-secondary">Auxiliary</strong>
                            )}
                            {frontColumn !== idx && backColumn !== idx && !auxColumns.includes(idx) && (
                                // <Form.Check defaultChecked={false} onChange={(e) => designateColumn(idx)}/>
                                <FormControlLabel control={<Checkbox />} onChange={() => designateColumn(idx)} label=""/>
                            )}
                        </Col>
                    ))}
                </Row>
                <hr/>
                <div className="deckCreationPreviewTable">
                    {deckContents.map((word, rowIdx) => (
                        <Row key={rowIdx} className="d-flex justify-content-evenly">
                            {word.parts.map((part) => (
                                <Col>{part}</Col>
                            ))}
                        </Row>
                    ))}
                </div>
                {frontColumn < 0 && <p className="fst-italic mt-3">Select front column to continue</p>}
                {frontColumn >= 0 && backColumn < 0 && <p className="fst-italic mt-3">Select back column to continue</p>}
                {backColumn >= 0 && deckContents[0].parts.length > 2 && <p className="fst-italic mt-3">{`${auxColumns.length} auxiliary columns selected. ${deckContents[0].parts.length - 2 - auxColumns.length} columns will be discarded`}</p>}
                {backColumn >= 0 && <MButton fullWidth={true} sx={{mt: 3}} variant="contained" onClick={() => setCurrentTab(3)}>Next</MButton>}
            </div>
        )
    }

    function wordSelectionView() {
        return (
            <div>
                <Row className="d-flex justify-content-evenly align-items-center">
                    <Col xs={1}/>
                    <Col xs={6} className="d-flex justify-content-between align-items-center">
                        <strong>Front</strong>
                        {/*<Button className="bi-arrow-left-right" onClick={wordSideSwapper}/>*/}
                    </Col>
                    <Col xs={5}><strong>Back</strong></Col>
                </Row>
                <hr/>
                <div className="deckCreationPreviewTable">
                    {currentTab === 3 && deckContents.filter(word => !(dontAddExistingCards && word.alreadyExists)).map((word, index) => (
                        <Row key={index} className="d-flex justify-content-evenly">
                            {/*<Col xs={1}><FormControlLabel control={<Checkbox defaultChecked={true}/>} onChange={(e) => handleWordCheckChanged(word)} label=""/></Col>*/}
                            <Col xs={1}><Form.Check defaultChecked={true} onChange={() => handleWordCheckChanged(word)}/></Col>
                            <Col xs={6}>{word.parts[frontColumn]}</Col>
                            <Col xs={5}>{word.parts[backColumn]}</Col>
                        </Row>
                    ))}
                </div>
                {!(loadingDeckToAddTo || !numWordsToCreate) &&
                    <MButton variant="contained" fullWidth={true} sx={{my: 4}} disabled={publishingDeck} onClick={saveNewWords}>Save {numWordsToCreate} words</MButton>
                }
                {publishingDeck && <div>
                    <BarLoader className="w-100" color={theme.palette.info.main} loading={true}/>
                    <p>uploading deck...</p>
                </div>}
                {publishingDeckErrorMessage && <p className="text-center text-danger fst-italic">{publishingDeckErrorMessage}</p>}
            </div>
        )
    }


}


export default DeckCreationWizard;